import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useCart } from '../../context/CartContext.jsx'
import { useAuth } from '../../context/AuthContext.jsx'
import {
  submitOrder,
  processPayment,
  createPaymentIntent,
} from '../../lib/orders.js'
import { money } from '../../lib/format.js'
import { trackInitiateCheckout, trackPurchase } from '../../lib/tracking.js'
import { validateReferral } from '../../lib/affiliates.js'
import { getStoredReferral } from '../../lib/referral.js'
import { tagadaEnabled } from '../../lib/tagada.js'
import { stripeEnabled } from '../../lib/stripe.js'
import { supabase } from '../../lib/supabaseClient.js'
import './checkout.css'

// Precedence for the card path: Stripe first, then TagadaPay, else pay-on-delivery.
const onlineCardEnabled = stripeEnabled || tagadaEnabled

// Lazy so the payment SDKs ship in their own chunks — only fetched when online
// payments are enabled and the shopper reaches the confirm step.
const CardPayment = lazy(() => import('../../components/CardPayment.jsx'))
const StripeCard = lazy(() => import('../../components/StripeCard.jsx'))

const STEPS = ['Details', 'Review', 'Confirm']
const HOLD_SECONDS = 15 * 60

// Persist the in-progress checkout for the browser session, so a shopper who
// navigates away (or reloads) keeps their delivery details, note, step, coupon,
// and intended-use selection. Cleared once the order is placed.
const CHECKOUT_KEY = 'renew.checkout'
function loadSavedCheckout() {
  try {
    return JSON.parse(sessionStorage.getItem(CHECKOUT_KEY)) || null
  } catch {
    return null
  }
}
function clearSavedCheckout() {
  try {
    sessionStorage.removeItem(CHECKOUT_KEY)
  } catch {
    /* sessionStorage unavailable */
  }
}

export default function Checkout() {
  const { items, subtotal, count, clear, setQty, remove } = useCart()
  const { user, profile } = useAuth()

  const saved = loadSavedCheckout()

  const [step, setStep] = useState(saved?.step ?? 0)
  const [form, setForm] = useState(
    saved?.form || {
      name: profile?.full_name || '',
      email: profile?.email || user?.email || '',
      phone: profile?.phone || '',
      street: profile?.address_street || '',
      city: profile?.address_city || '',
      state: profile?.address_state || '',
      zip: profile?.address_postal || '',
      note: '',
    }
  )
  const [coupon, setCoupon] = useState(saved?.coupon || '')
  const [couponMsg, setCouponMsg] = useState('')
  const [discount, setDiscount] = useState(null) // { code, percent, name } | null
  const [smsConsent, setSmsConsent] = useState(saved?.smsConsent || false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(null)
  const [secondsLeft, setSecondsLeft] = useState(HOLD_SECONDS)

  const set = (f) => (e) => setForm((s) => ({ ...s, [f]: e.target.value }))

  // Save the in-progress checkout to the browser session on every change, so
  // leaving and returning to the page restores everything. Skipped once the
  // order is confirmed (done), since we clear it then.
  useEffect(() => {
    if (done) return
    try {
      sessionStorage.setItem(
        CHECKOUT_KEY,
        JSON.stringify({ form, coupon, step, smsConsent })
      )
    } catch {
      /* sessionStorage unavailable — form simply won't persist */
    }
  }, [form, coupon, step, smsConsent, done])

  // Prefill contact + address from the signed-in user's profile once it loads.
  // Only fills fields that are still empty, so it never overwrites a restored
  // session or anything the shopper has already typed.
  useEffect(() => {
    if (!profile) return
    setForm((f) => ({
      ...f,
      name: f.name || profile.full_name || '',
      email: f.email || profile.email || user?.email || '',
      phone: f.phone || profile.phone || '',
      street: f.street || profile.address_street || '',
      city: f.city || profile.address_city || '',
      state: f.state || profile.address_state || '',
      zip: f.zip || profile.address_postal || '',
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile])

  // Fallback prefill: for returning customers whose profile has no address yet,
  // pull name/phone/address from their most recent order (address lives in the
  // order note as "Deliver to: street, city, state zip"). Only fills empties.
  useEffect(() => {
    if (!user) return
    let active = true
    ;(async () => {
      try {
        const { data } = await supabase
          .from('orders')
          .select('customer_name, customer_phone, note')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
        const last = data?.[0]
        if (!last || !active) return
        // Parse "Deliver to: STREET, CITY, STATE ZIP" from the note.
        let street = '', city = '', state = '', zip = ''
        const line = (last.note || '')
          .split('\n')
          .find((l) => l.trim().toLowerCase().startsWith('deliver to:'))
        if (line) {
          const body = line.replace(/deliver to:/i, '').trim()
          const parts = body.split(',').map((s) => s.trim()).filter(Boolean)
          if (parts.length >= 3) {
            const sz = parts[parts.length - 1].split(/\s+/)
            zip = sz.pop() || ''
            state = sz.join(' ')
            city = parts[parts.length - 2]
            street = parts.slice(0, parts.length - 2).join(', ')
          }
        }
        setForm((f) => ({
          ...f,
          name: f.name || last.customer_name || '',
          phone: f.phone || last.customer_phone || '',
          street: f.street || street,
          city: f.city || city,
          state: f.state || state,
          zip: f.zip || zip,
        }))
      } catch {
        /* prefill convenience only */
      }
    })()
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Save the entered address back to the signed-in user's profile so it prefills
  // next time. Best-effort — never blocks the order.
  const saveAddressToProfile = async () => {
    if (!user) return
    try {
      await supabase
        .from('profiles')
        .update({
          phone: form.phone.trim(),
          address_street: form.street.trim(),
          address_city: form.city.trim(),
          address_state: form.state.trim(),
          address_postal: form.zip.trim(),
        })
        .eq('id', user.id)
    } catch {
      /* ignore — prefill convenience only */
    }
  }

  // Handle the return from Stripe hosted Checkout (?stripe=success|cancel).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const s = params.get('stripe')
    if (!s) return
    if (s === 'success') {
      const orderNumber = params.get('order') || ''
      trackPurchase({ items, total: totalDue, orderNumber })
      clear()
      clearSavedCheckout()
      setDone({ order_number: orderNumber, paid: true })
    } else if (s === 'cancel') {
      setError('Payment was canceled — your cart is still here. You can try again.')
    }
    // Clean the query string so a refresh doesn't re-trigger.
    window.history.replaceState({}, '', '/checkout')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fire InitiateCheckout once when a real cart loads.
  useEffect(() => {
    if (count > 0) trackInitiateCheckout(items, subtotal)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cart-hold countdown.
  useEffect(() => {
    if (done || count === 0) return
    const t = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(t)
  }, [done, count])

  const timer = useMemo(() => {
    const m = Math.floor(secondsLeft / 60)
    const s = secondsLeft % 60
    return `${m}:${String(s).padStart(2, '0')}`
  }, [secondsLeft])

  const detailsValid =
    form.name.trim() &&
    form.email.trim() &&
    form.phone.trim() &&
    form.street.trim() &&
    form.city.trim() &&
    form.state.trim() &&
    form.zip.trim()

  // Validate a code against the affiliate list and apply its discount.
  const applyCode = async (raw) => {
    const code = (raw || '').trim()
    if (!code) {
      setDiscount(null)
      setCouponMsg('Enter a code to apply.')
      return
    }
    const r = await validateReferral(code)
    if (r?.valid) {
      setDiscount({ code: r.code, percent: r.discount_percent, name: r.name })
      setCoupon(r.code)
      setCouponMsg(
        `Code applied — ${r.discount_percent}% off${r.name ? `, credited to ${r.name}` : ''}.`
      )
    } else {
      setDiscount(null)
      setCouponMsg('That code isn’t valid.')
    }
  }

  const applyCoupon = () => applyCode(coupon)

  // On mount, re-apply a saved coupon (from a restored session) or a referral
  // code carried in from a rep's link (?ref=CODE).
  useEffect(() => {
    const code = saved?.coupon || getStoredReferral()
    if (code) applyCode(code)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const discountAmount = discount
    ? Math.round(subtotal * (discount.percent / 100) * 100) / 100
    : 0
  const totalDue = Math.max(0, Math.round((subtotal - discountAmount) * 100) / 100)

  const buildNote = () =>
    [
      `Deliver to: ${form.street}, ${form.city}, ${form.state} ${form.zip}`,
      form.note.trim() && `Note: ${form.note.trim()}`,
    ]
      .filter(Boolean)
      .join('\n')

  const customerPayload = () => ({
    name: form.name.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    note: buildNote(),
  })

  // Pay-on-delivery: record the order, no payment taken online.
  const placeOrder = async () => {
    setError('')
    setBusy(true)
    try {
      const result = await submitOrder({
        customer: customerPayload(),
        items,
        userId: user?.id,
        referralCode: discount?.code || null,
        smsConsent,
      })
      trackPurchase({ items, total: totalDue, orderNumber: result.order_number })
      saveAddressToProfile()
      clear()
      clearSavedCheckout()
      setDone({ order_number: result.order_number, paid: false })
    } catch (err) {
      setError(err.message || 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  // Online card payment (Stripe embedded Payment Element). <StripeCard> calls
  // this to create the server-side PaymentIntent, then confirms the card on-page.
  const createStripeIntent = () =>
    createPaymentIntent({
      customer: customerPayload(),
      items,
      userId: user?.id,
      referralCode: discount?.code || null,
      fulfillment: 'delivery',
      zip: form.zip,
      smsConsent,
    })

  // Called by <StripeCard> once the card is confirmed. The paid order is recorded
  // by the webhook; here we just finish the client-side flow.
  const onStripePaid = (orderNumber) => {
    trackPurchase({ items, total: totalDue, orderNumber })
    saveAddressToProfile()
    clear()
    clearSavedCheckout()
    setDone({ order_number: orderNumber, paid: true })
  }

  // Online card payment (TagadaPay). Called by <CardPayment> with a card token.
  const payOnline = async ({ tagadaToken, scaRequired, sessionData }) => {
    setError('')
    setBusy(true)
    try {
      const result = await processPayment({
        customer: customerPayload(),
        items,
        userId: user?.id,
        referralCode: discount?.code || null,
        tagadaToken,
        scaRequired,
        sessionData,
        fulfillment: 'delivery',
        zip: form.zip,
        smsConsent,
      })
      // Rare: card needs extra 3-D Secure authentication — send them to finish it.
      if (result?.requireAction === 'redirect' && result.redirectUrl) {
        window.location.href = result.redirectUrl
        return
      }
      trackPurchase({ items, total: totalDue, orderNumber: result.order_number })
      saveAddressToProfile()
      clear()
      clearSavedCheckout()
      setDone({ order_number: result.order_number, paid: true })
    } catch (err) {
      if (err.notConfigured) {
        // Server not wired for online payment yet — fall back to pay-on-delivery.
        await placeOrder()
        return
      }
      setError(err.message || 'Your payment could not be processed.')
    } finally {
      setBusy(false)
    }
  }

  // Confirmation
  if (done) {
    return (
      <div className="checkout">
        <div className="container checkout__confirm">
          <span className="checkout__check" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="30" height="30" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </span>
          <h1>Order received</h1>
          <p>
            Your order <strong>{done.order_number}</strong> is in.{' '}
            {done.paid
              ? 'Your payment was received — a receipt is on its way to your inbox. We’ll reach out to arrange your delivery.'
              : 'We’ll reach out to arrange your delivery. No payment is taken online — you’ll pay on delivery.'}
          </p>
          <p className="checkout__confirm-contact">
            Questions? Reply to your order confirmation email.
          </p>
          <Link to="/products" className="btn btn--primary">
            Continue browsing
          </Link>
        </div>
      </div>
    )
  }

  // Empty cart
  if (count === 0) {
    return (
      <div className="checkout">
        <div className="container checkout__confirm">
          <h1>Your cart is empty</h1>
          <p>Add some research compounds before checking out.</p>
          <Link to="/products" className="btn btn--primary">
            Browse products
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="checkout">
      <header className="checkout__hero deco-band">
        <div className="container checkout__hero-row">
          <div>
            <span className="eyebrow">Checkout</span>
            <h1>Complete your order</h1>
          </div>
          <div className="checkout__timer" aria-live="polite">
            Cart reserved for <strong>{timer}</strong>
          </div>
        </div>
      </header>

      <div className="container checkout__grid">
        <div className="checkout__main">
          <ol className="checkout__steps-nav">
            {STEPS.map((s, i) => {
              // Going back is always allowed; going forward needs valid details.
              const reachable = i <= step || detailsValid
              return (
                <li
                  key={s}
                  className={`checkout__stepitem ${i === step ? 'is-active' : ''} ${
                    i < step ? 'is-done' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="checkout__steplink"
                    onClick={() => reachable && !busy && setStep(i)}
                    disabled={!reachable || busy}
                    aria-current={i === step ? 'step' : undefined}
                  >
                    <span className="checkout__stepnum">{i + 1}</span>
                    {s}
                  </button>
                </li>
              )
            })}
          </ol>

          {error && <div className="auth__alert auth__alert--error">{error}</div>}

          {/* Step 1 — Delivery details */}
          {step === 0 && (
            <div className="checkout__form">
              <h2>Delivery details</h2>
              <label>
                Full Name
                <input value={form.name} onChange={set('name')}
                  placeholder="Jane Doe" autoComplete="name" />
              </label>
              <div className="checkout__row">
                <label>
                  Email
                  <input type="email" value={form.email} onChange={set('email')}
                    placeholder="you@example.com" autoComplete="email" />
                </label>
                <label>
                  Phone
                  <input value={form.phone} onChange={set('phone')}
                    placeholder="(555) 123-4567" autoComplete="tel" />
                </label>
              </div>
              <label>
                Street Address
                <input value={form.street} onChange={set('street')}
                  placeholder="123 Research Blvd" autoComplete="address-line1" />
              </label>
              <div className="checkout__row checkout__row--3">
                <label>
                  City
                  <input value={form.city} onChange={set('city')}
                    placeholder="Las Vegas" autoComplete="address-level2" />
                </label>
                <label>
                  State
                  <input value={form.state} onChange={set('state')}
                    placeholder="NV" autoComplete="address-level1" />
                </label>
                <label>
                  ZIP
                  <input value={form.zip} onChange={set('zip')}
                    placeholder="89101" autoComplete="postal-code" />
                </label>
              </div>

              <label className="checkout__sms-consent">
                <input
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                />
                <span>
                  Text me offers and updates from Renew Labs LV. Msg &amp; data
                  rates may apply, frequency varies, reply STOP to opt out.
                  Consent is not a condition of purchase.
                </span>
              </label>

              <button
                className="btn btn--primary btn--block"
                disabled={!detailsValid}
                onClick={() => setStep(1)}
              >
                Continue to review
              </button>
            </div>
          )}

          {/* Step 2 — Review */}
          {step === 1 && (
            <div className="checkout__form">
              <h2>Review your order</h2>
              <div className="checkout__review-block">
                <div className="checkout__review-head">
                  <span>Delivering to</span>
                  <button className="checkout__edit" onClick={() => setStep(0)}>
                    Edit
                  </button>
                </div>
                <p className="checkout__review-addr">
                  {form.name}
                  <br />
                  {form.street}, {form.city}, {form.state} {form.zip}
                  <br />
                  {form.email} · {form.phone}
                </p>
              </div>

              <label>
                Order note <span className="checkout__optional">Optional</span>
                <textarea rows={3} value={form.note} onChange={set('note')}
                  placeholder="Preferred delivery time, gate code, etc." />
              </label>

              <div className="checkout__coupon">
                <input
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  placeholder="Promo code"
                />
                <button type="button" className="btn btn--outline" onClick={applyCoupon}>
                  Apply
                </button>
              </div>
              {couponMsg && <p className="checkout__coupon-msg">{couponMsg}</p>}

              <div className="checkout__step-actions">
                <button className="btn btn--outline" onClick={() => setStep(0)}>
                  Back
                </button>
                <button className="btn btn--primary" onClick={() => setStep(2)}>
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3 — Confirm (pay on delivery) */}
          {step === 2 && (
            <div className="checkout__form">
              <h2>Confirm your order</h2>

              <div className="checkout__pay-notice">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
                  stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
                  strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <path d="M2 10h20" />
                </svg>
                <div>
                  {onlineCardEnabled ? (
                    <>
                      <strong>Pay securely by card</strong>
                      <span>
                        We accept Visa, Mastercard, and American Express. Your
                        card is charged now and we’ll arrange your delivery.
                      </span>
                    </>
                  ) : (
                    <>
                      <strong>Pay on delivery</strong>
                      <span>
                        No payment is taken online — you’ll pay when your order
                        arrives. We’ll reach out within 24 hours to arrange it.
                      </span>
                    </>
                  )}
                </div>
              </div>

              {!onlineCardEnabled && (
                <div className="checkout__pay-methods">
                  <div className="checkout__pay-methods-head">
                    <span>Accepted at delivery</span>
                    <span className="checkout__pay-methods-secure">
                      <svg viewBox="0 0 24 24" width="13" height="13" fill="none"
                        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                        strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
                        <path d="M9 12l2 2 4-4" />
                      </svg>
                      No payment online
                    </span>
                  </div>
                  <div className="checkout__pay-methods-grid">
                    <div className="checkout__pay-method">
                      <img className="checkout__pay-img" src="/pay/zelle.png" alt="Zelle" />
                    </div>
                    <div className="checkout__pay-method">
                      <img className="checkout__pay-img" src="/pay/venmo.png" alt="Venmo" />
                    </div>
                    <div className="checkout__pay-method">
                      <span className="checkout__pay-word checkout__pay-word--cash">
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                          strokeLinejoin="round" aria-hidden="true">
                          <rect x="2" y="6" width="20" height="12" rx="2" />
                          <circle cx="12" cy="12" r="2.5" />
                        </svg>
                        Cash
                      </span>
                    </div>
                  </div>
                  <p className="checkout__pay-methods-note">
                    Pay by Zelle, Venmo, or cash when your order is handed to you —
                    nothing is charged online.
                  </p>
                </div>
              )}

              {stripeEnabled ? (
                <>
                  <Suspense fallback={<p className="checkout__coupon-msg">Loading secure payment…</p>}>
                    <StripeCard
                      amount={Math.round(totalDue * 100)}
                      amountLabel={money(totalDue)}
                      canPay
                      submitting={busy}
                      createIntent={createStripeIntent}
                      onPaid={onStripePaid}
                      onError={setError}
                    />
                  </Suspense>
                  <div className="checkout__step-actions checkout__step-actions--single">
                    <button className="btn btn--outline" onClick={() => setStep(1)} disabled={busy}>
                      Back
                    </button>
                  </div>
                  <p className="checkout__disclaimer">
                    For research use only. Not for human consumption.
                  </p>
                </>
              ) : tagadaEnabled ? (
                <>
                  <Suspense fallback={<p className="checkout__coupon-msg">Loading secure payment…</p>}>
                    <CardPayment
                      amountLabel={money(totalDue)}
                      canPay
                      submitting={busy}
                      onPay={payOnline}
                      onError={setError}
                    />
                  </Suspense>
                  <div className="checkout__step-actions checkout__step-actions--single">
                    <button className="btn btn--outline" onClick={() => setStep(1)} disabled={busy}>
                      Back
                    </button>
                  </div>
                  <p className="checkout__disclaimer">
                    For research use only. Not for human consumption.
                  </p>
                </>
              ) : (
                <>
                  <div className="checkout__step-actions">
                    <button className="btn btn--outline" onClick={() => setStep(1)} disabled={busy}>
                      Back
                    </button>
                    <button
                      className="btn btn--primary"
                      onClick={placeOrder}
                      disabled={busy}
                    >
                      {busy ? 'Placing order…' : 'Place order'}
                    </button>
                  </div>
                  <p className="checkout__disclaimer">
                    For research use only. Not for human consumption. Payment is
                    collected in person on delivery.
                  </p>
                </>
              )}

              {/* Reassurance block */}
              <div className="checkout__trust">
                <div className="checkout__trust-item">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
                    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
                    strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                  <div>
                    <strong>Third-party tested</strong>
                    <span>Every batch HPLC + LC-MS verified with a certificate of analysis.</span>
                  </div>
                </div>
                <div className="checkout__trust-item">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
                    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
                    strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 21s7-5.2 7-11a7 7 0 0 0-14 0c0 5.8 7 11 7 11z" />
                    <circle cx="12" cy="10" r="2.5" />
                  </svg>
                  <div>
                    <strong>Local, from our Las Vegas lab</strong>
                    <span>Packed and checked by hand, delivered by real people.</span>
                  </div>
                </div>
                <div className="checkout__trust-item">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
                    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
                    strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <div>
                    <strong>Real people, real support</strong>
                    <span>A Las Vegas team you can reach by phone or email — order questions welcome.</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <aside className="checkout__summary">
          <h2>Order summary</h2>
          <div className="checkout__items">
            {items.map((i) => (
              <div className="checkout__item" key={i.id}>
                <div className="checkout__item-top">
                  <span className="checkout__item-name">{i.name}</span>
                  <span className="checkout__item-price">{money(i.price * i.qty)}</span>
                </div>
                <div className="checkout__item-controls">
                  <div className="checkout__qty">
                    <button
                      type="button"
                      onClick={() => setQty(i.id, i.qty - 1)}
                      aria-label={`Decrease ${i.name}`}
                    >
                      −
                    </button>
                    <span aria-live="polite">{i.qty}</span>
                    <button
                      type="button"
                      onClick={() => setQty(i.id, i.qty + 1)}
                      aria-label={`Increase ${i.name}`}
                    >
                      +
                    </button>
                  </div>
                  <button
                    type="button"
                    className="checkout__item-remove"
                    onClick={() => remove(i.id)}
                    aria-label={`Remove ${i.name}`}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
          {discount && (
            <div className="checkout__summary-line">
              <span>Subtotal</span>
              <span>{money(subtotal)}</span>
            </div>
          )}
          {discount && (
            <div className="checkout__summary-line checkout__summary-line--discount">
              <span>Discount ({discount.code} · {discount.percent}%)</span>
              <span>−{money(discountAmount)}</span>
            </div>
          )}
          <div className="checkout__total">
            <span>{onlineCardEnabled ? 'Total' : 'Total due on delivery'}</span>
            <strong>{money(totalDue)}</strong>
          </div>
          <Link to="/products" className="checkout__back">
            ← Add more items
          </Link>
        </aside>
      </div>
    </div>
  )
}
